import React, {Fragment, useCallback, useEffect, useState} from "react";
import {Box} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {connect} from "react-redux";
import {v4 as uuidv4} from "uuid";
import Call from "../../../hocs/call";
import SingleViewerFooter from "./Footer";
import SingleViewerHeader from "./Header";
import SingleViewerSidebar from "./Sidebar";
import SingleViewerViewer from "./Viewer";
import {
  backupDatasetSVState,
  fetchDatasetSVDataset,
  hideDatasetSVDownloadWarning,
  hideDatasetSVUnavailableViewWarning,
  setDatasetSVFullScreen
} from "../../../state/dataset/single-viewer/actions";
import {getDatasetStorageKey} from "../../../utils/other";

const $ = window.jQuery;

const mapStateToProps = ({catalog, dataset}) => ({
  datasetMap: (catalog.uncategorizedDatasets || []).reduce(
    (acc, v) => ({...acc, [v.identifier]: v}),
    catalog.datasetMap || {}
  ),
  datasetUuid: dataset.singleViewer.datasetUuid,
  isFetchDatasetDisabled: dataset.singleViewer.isFetchDatasetDisabled,
  criteria: dataset.singleViewer.criteria,
  isDownloadWarningVisible: dataset.singleViewer.isDownloadWarningVisible,
  isUnavailableViewWarningVisible: dataset.singleViewer.isUnavailableViewWarningVisible,
  isFullscreen: dataset.singleViewer.isFullscreen,
  lastNObservations: dataset.singleViewer.lastNObservations
});

const mapDispatchToProps = dispatch => ({
  onDatasetFetch: ({nodeId, datasetId, datasetTitle, criteria, lastNObservations}) =>
    dispatch(fetchDatasetSVDataset(nodeId, datasetId, datasetTitle, criteria, lastNObservations)),
  onDownloadWarningHide: () => dispatch(hideDatasetSVDownloadWarning()),
  onUnavailableViewHide: () => dispatch(hideDatasetSVUnavailableViewWarning()),
  onSetFullscreen: isFullscreen => dispatch(setDatasetSVFullScreen(isFullscreen)),
  onDatasetBackup: datasetUuid => dispatch(backupDatasetSVState(datasetUuid))
});

const handleStyle = () => {
  const headerHeight = document.getElementById("data-viewer__header")?.offsetHeight || 0;
  const footerHeight = document.getElementById("data-viewer__footer")?.offsetHeight || 0;
  $("#data-viewer__sidebar-viewer-container").height(`calc(100% - ${headerHeight + footerHeight}px)`);

  const sidebarWidth = document.getElementById("data-viewer__sidebar")?.offsetWidth || 0;
  $("#data-viewer__viewer").width(`calc(100% - ${sidebarWidth}px)`);
};

function SingleViewer(props) {
  const {
    nodeId,
    nodeCode,
    categoryPath,
    datasetId,
    datasetTitle,
    viewId,
    attachedFiles,
    nodeExtras,

    datasetMap,
    datasetUuid,
    isFetchDatasetDisabled,
    criteria,
    isDownloadWarningVisible,
    isUnavailableViewWarningVisible,
    isFullscreen,
    lastNObservations,

    onDatasetFetch,
    onDownloadWarningHide,
    onUnavailableViewHide,
    onSetFullscreen,
    onDatasetBackup
  } = props;

  const {t} = useTranslation();

  const [chartId] = useState("chart__" + uuidv4());

  const [mapId] = useState("map__" + uuidv4());

  useEffect(() => {
    return () => {
      const storedDatasets = JSON.parse(sessionStorage.getItem("datasets") || "[]");
      if (datasetUuid && storedDatasets.map(getDatasetStorageKey).includes(datasetUuid)) {
        onDatasetBackup(datasetUuid);
      }
    };
  }, [datasetUuid, onDatasetBackup]);

  useEffect(() => {
    window.addEventListener("resize", handleStyle);
    return () => window.removeEventListener("resize", handleStyle);
  }, []);

  useEffect(() => {
    handleStyle();
  });

  const onToggleFullScreen = useCallback(() => {
    const isEnteringFullscreen = !isFullscreen;

    const appContainer = document.getElementById("app-container");
    appContainer.style.visibility = isEnteringFullscreen ? "hidden" : "";
    const fullscreenContainer = document.getElementById("fullscreen-container");
    fullscreenContainer.style.display = isEnteringFullscreen ? "block" : "none";

    onSetFullscreen(isEnteringFullscreen);
  }, [isFullscreen, onSetFullscreen]);

  return (
    <Fragment>
      {createPortal(
        <Box
          id="data-viewer"
          sx={{
            width: "100%",
            height: "100%"
          }}
          className={` data-viewer__single-viewer`}
        >
          <Call
            cb={onDatasetFetch}
            cbParam={{
              nodeId,
              datasetId,
              datasetTitle,
              criteria,
              lastNObservations
            }}
            disabled={!criteria || isFetchDatasetDisabled}
          >
            <Box
              id="data-viewer__header"
              sx={{width: "100%"}}
              style={{padding: isFullscreen ? "0 0 16px 0" : "4px 16px 12px"}}
            >
              <SingleViewerHeader
                nodeId={nodeId}
                nodeCode={nodeCode}
                nodeExtras={nodeExtras}
                datasetId={datasetId}
                datasetTitle={datasetTitle}
                viewId={viewId}
                attachedFiles={attachedFiles}
                chartId={chartId}
                mapId={mapId}
                onRender={handleStyle}
                datasetMap={datasetMap}
                onToggleFullScreen={onToggleFullScreen}
              />
            </Box>
            <Box
              id="data-viewer__sidebar-viewer-container"
              sx={{
                display: "flex",
                width: "100%",
                height: "100%",
                minHeight: "400px",
                minWidth: "560px"
              }}
              style={{padding: isFullscreen ? "0" : "0 16px"}}
            >
              <Box id="data-viewer__sidebar" sx={{height: "100%"}} style={{display: isFullscreen ? "none" : "block"}}>
                <SingleViewerSidebar
                  nodeId={nodeId}
                  nodeCode={nodeCode}
                  nodeExtras={nodeExtras}
                  datasetId={datasetId}
                  datasetTitle={datasetTitle}
                  chartId={chartId}
                  mapId={mapId}
                  datasetMap={datasetMap}
                />
              </Box>
              <Box id="data-viewer__viewer" sx={{height: "100%"}}>
                <SingleViewerViewer
                  nodeId={nodeId}
                  nodeExtras={nodeExtras}
                  datasetId={datasetId}
                  datasetTitle={datasetTitle}
                  chartId={chartId}
                  mapId={mapId}
                />
              </Box>
            </Box>
            <Box
              id="data-viewer__footer"
              sx={{
                width: "100%",
                overflowX: "auto",
                overflowY: "hidden",
                padding: "8px"
              }}
              style={{display: isFullscreen ? "none" : "block"}}
            >
              <SingleViewerFooter
                dataset={{
                  nodeCode: nodeCode,
                  categoryPath: categoryPath,
                  datasetId: datasetId,
                  datasetTitle: datasetTitle,
                  viewId: viewId
                }}
                onDatasetsChange={handleStyle}
              />
            </Box>
          </Call>
        </Box>,
        !isFullscreen
          ? document.getElementById("data-viewer-container")
          : document.getElementById("fullscreen-container")
      )}

      <Dialog open={isDownloadWarningVisible} maxWidth="md" onClose={onDownloadWarningHide}>
        <DialogContent>{t("scenes.dataViewer.dialogs.downloadFormat.content")}</DialogContent>
        <DialogActions>
          <Button onClick={onDownloadWarningHide}>{t("commons.confirm.confirm")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isUnavailableViewWarningVisible} maxWidth="md" onClose={onUnavailableViewHide}>
        <DialogContent>{t("scenes.dataViewer.dialogs.unavailableView.content")}</DialogContent>
        <DialogActions>
          <Button onClick={onUnavailableViewHide}>{t("commons.confirm.confirm")}</Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(SingleViewer);
